import React, { useState } from 'react'
import clsx from 'clsx'
import {
  addYears,
  addMonths,
  setMonth,
  subMonths,
  subYears,
  startOfDecade,
  endOfDecade,
  setYear,
} from 'date-fns'

import { IconArrowDown } from 'components/Icons'

import DateCalendar from './DateCalendar'
import MonthCalendar from './MonthCalendar'
import YearCalendar from './YearCalendar'
import format from 'libs/dateFns/format'

export interface CalendarProps {
  value: Date | null
  onChange: (value: Date) => void
  className?: string
  disabledDate?: (currentDate: Date) => boolean
}

export enum CALENDAR_TYPE {
  DAY,
  MONTH,
  YEAR,
}

export const Calendar: React.FC<CalendarProps> = ({
  value,
  onChange,
  className,
  disabledDate = () => false,
}: CalendarProps): JSX.Element => {
  const base = 'calendar'
  const _value = value || new Date()

  const [displayDate, setDisplayDate] = useState<Date>(_value)
  const [calendarType, setCalendarType] = useState<CALENDAR_TYPE>(
    CALENDAR_TYPE.DAY,
  )

  const _onChange = (value: Date) => {
    setDisplayDate(value)
    onChange(value)
  }

  const onMonthChange = (value: Date) => {
    const next = value.getMonth()

    setDisplayDate(setMonth(displayDate, next))
    setCalendarType(CALENDAR_TYPE.DAY)
  }

  const onYearChange = (value: Date) => {
    const next = value.getFullYear()

    setDisplayDate(setYear(displayDate, next))
    setCalendarType(CALENDAR_TYPE.MONTH)
  }

  const handlePrev = (calendarType: CALENDAR_TYPE) => {
    switch (calendarType) {
      case CALENDAR_TYPE.MONTH:
        setDisplayDate(subYears(displayDate, 1))
        break
      case CALENDAR_TYPE.YEAR:
        setDisplayDate(subYears(displayDate, 10))
        break
      default:
        setDisplayDate(subMonths(displayDate, 1))
    }
  }

  const handleNext = (calendarType: CALENDAR_TYPE) => {
    switch (calendarType) {
      case CALENDAR_TYPE.MONTH:
        setDisplayDate(addYears(displayDate, 1))
        break
      case CALENDAR_TYPE.YEAR:
        setDisplayDate(addYears(displayDate, 10))
        break
      default:
        setDisplayDate(addMonths(displayDate, 1))
    }
  }

  const renderController = (calendarType: CALENDAR_TYPE) => {
    switch (calendarType) {
      case CALENDAR_TYPE.MONTH:
        return (
          <div
            onClick={() => setCalendarType(CALENDAR_TYPE.YEAR)}
            className={`${base}__month-year`}
            data-testid='calendar-year-button'
          >
            {format(displayDate, 'yyyy')}
          </div>
        )
      case CALENDAR_TYPE.YEAR:
        const startDecade = startOfDecade(displayDate).getFullYear()
        const endDecade = endOfDecade(displayDate).getFullYear()

        return (
          <div
            onClick={() => setCalendarType(CALENDAR_TYPE.DAY)}
            className={`${base}__month-year`}
            data-testid='calendar-decade-button'
          >
            {`${startDecade}-${endDecade}`}
          </div>
        )
      default:
        return (
          <>
            <div
              onClick={() => setCalendarType(CALENDAR_TYPE.MONTH)}
              className={`${base}__month-year`}
              data-testid='calendar-month-button'
            >
              {format(displayDate, 'LLLL')}
            </div>
            <div
              onClick={() => setCalendarType(CALENDAR_TYPE.YEAR)}
              className={`${base}__month-year`}
              data-testid='calendar-year-button'
            >
              {format(displayDate, 'yyyy')}
            </div>
          </>
        )
    }
  }

  const renderCalendar = (calendarType: CALENDAR_TYPE) => {
    switch (calendarType) {
      case CALENDAR_TYPE.MONTH:
        return (
          <MonthCalendar
            value={value}
            displayDate={displayDate}
            onChange={onMonthChange}
          />
        )
      case CALENDAR_TYPE.YEAR:
        return (
          <YearCalendar
            value={value}
            displayDate={displayDate}
            onChange={onYearChange}
          />
        )
      default:
        return (
          <DateCalendar
            value={value}
            displayDate={displayDate}
            onChange={_onChange}
            disabledDate={disabledDate}
          />
        )
    }
  }

  return (
    <div className={clsx(base, className)}>
      <IconArrowDown
        variants='sm'
        className={`${base}__arrow ${base}__arrow--left`}
        onClick={() => handlePrev(calendarType)}
        data-testid='calendar-prev'
      />
      <div className={`${base}__month-year-con`}>
        {renderController(calendarType)}
      </div>
      <IconArrowDown
        className={`${base}__arrow  ${base}__arrow--right`}
        variants='sm'
        onClick={() => handleNext(calendarType)}
        data-testid='calendar-next'
      />
      {renderCalendar(calendarType)}
    </div>
  )
}

export default Calendar
